import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { 
  Mic, 
  MicOff, 
  Video, 
  VideoOff, 
  Settings,
  Loader2,
  AlertCircle,
  ChevronDown,
  Check
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface MediaPreviewProps {
  onStart: (config: {
    audioEnabled: boolean;
    videoEnabled: boolean;
    selectedAudioInput: string;
    selectedVideoInput: string;
    selectedAudioOutput: string;
  }) => void;
  mode: 'voice' | 'video';
  moduleTitle: string;
  coachName: string;
}

export function MediaPreview({ onStart, mode, moduleTitle, coachName }: MediaPreviewProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isAudioEnabled, setIsAudioEnabled] = useState(true);
  const [isVideoEnabled, setIsVideoEnabled] = useState(mode === 'video');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  
  // Device selection states
  const [audioInputs, setAudioInputs] = useState<MediaDeviceInfo[]>([]);
  const [videoInputs, setVideoInputs] = useState<MediaDeviceInfo[]>([]);
  const [audioOutputs, setAudioOutputs] = useState<MediaDeviceInfo[]>([]);
  const [selectedAudioInput, setSelectedAudioInput] = useState<string>('');
  const [selectedVideoInput, setSelectedVideoInput] = useState<string>('');
  const [selectedAudioOutput, setSelectedAudioOutput] = useState<string>('');
  const [showSettings, setShowSettings] = useState(false);
  
  // Audio level monitoring
  const [audioLevel, setAudioLevel] = useState(0);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);

  // Get available devices
  const getDevices = async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      
      const audioIn = devices.filter(device => device.kind === 'audioinput');
      const videoIn = devices.filter(device => device.kind === 'videoinput');
      const audioOut = devices.filter(device => device.kind === 'audiooutput');
      
      setAudioInputs(audioIn);
      setVideoInputs(videoIn);
      setAudioOutputs(audioOut);
      
      // Set default devices
      if (audioIn.length > 0 && !selectedAudioInput) {
        setSelectedAudioInput(audioIn[0].deviceId);
      }
      if (videoIn.length > 0 && !selectedVideoInput) {
        setSelectedVideoInput(videoIn[0].deviceId);
      }
      if (audioOut.length > 0 && !selectedAudioOutput) {
        setSelectedAudioOutput(audioOut[0].deviceId);
      }
    } catch (err) {
      console.error('Error enumerating devices:', err);
    }
  };

  // Monitor audio levels
  const startAudioMonitoring = (stream: MediaStream) => {
    try {
      audioContextRef.current = new AudioContext();
      analyserRef.current = audioContextRef.current.createAnalyser();
      const source = audioContextRef.current.createMediaStreamSource(stream);
      source.connect(analyserRef.current);
      
      analyserRef.current.fftSize = 256;
      const bufferLength = analyserRef.current.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);
      
      const checkAudioLevel = () => {
        if (!analyserRef.current) return;
        
        analyserRef.current.getByteFrequencyData(dataArray);
        const average = dataArray.reduce((a, b) => a + b) / bufferLength;
        setAudioLevel(average / 255); // Normalize to 0-1
        
        animationFrameRef.current = requestAnimationFrame(checkAudioLevel);
      };
      
      checkAudioLevel();
    } catch (err) {
      console.error('Error starting audio monitoring:', err);
    }
  };

  // Initialize media stream
  const initializeMedia = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Stop existing stream
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
      
      const constraints: MediaStreamConstraints = {
        audio: isAudioEnabled ? {
          deviceId: selectedAudioInput ? { exact: selectedAudioInput } : undefined,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        } : false,
        video: mode === 'video' && isVideoEnabled ? {
          deviceId: selectedVideoInput ? { exact: selectedVideoInput } : undefined,
          width: { ideal: 1280 },
          height: { ideal: 720 }
        } : false
      };
      
      const newStream = await navigator.mediaDevices.getUserMedia(constraints);
      setStream(newStream);
      
      // Set video stream
      if (videoRef.current && mode === 'video' && isVideoEnabled) {
        videoRef.current.srcObject = newStream;
      }
      
      // Start audio monitoring
      if (isAudioEnabled) {
        startAudioMonitoring(newStream);
      }
      
      setIsLoading(false);
    } catch (err) {
      console.error('Error accessing media devices:', err);
      setError(err instanceof Error ? err.message : 'Failed to access media devices');
      setIsLoading(false);
    }
  };

  // Clean up
  useEffect(() => {
    getDevices();
    initializeMedia();
    
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-initialize when device selection changes
  useEffect(() => {
    if (selectedAudioInput || selectedVideoInput) {
      initializeMedia();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAudioInput, selectedVideoInput, isAudioEnabled, isVideoEnabled]);

  const toggleAudio = () => {
    setIsAudioEnabled(!isAudioEnabled);
    if (stream) {
      stream.getAudioTracks().forEach(track => {
        track.enabled = !isAudioEnabled;
      });
    }
  };

  const toggleVideo = () => {
    if (mode === 'video') {
      setIsVideoEnabled(!isVideoEnabled);
      if (stream) {
        stream.getVideoTracks().forEach(track => {
          track.enabled = !isVideoEnabled;
        });
      }
    }
  };

  const handleStart = () => {
    // Clean up preview stream
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
    }
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
    }
    
    onStart({
      audioEnabled: isAudioEnabled,
      videoEnabled: isVideoEnabled && mode === 'video',
      selectedAudioInput,
      selectedVideoInput,
      selectedAudioOutput
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-xl border border-gray-200 max-w-4xl w-full overflow-hidden"
      >
        {/* Header */}
        <div className="bg-primary p-6 text-white">
          <h1 className="text-2xl font-bold mb-1">Set up your {mode === 'video' ? 'camera and microphone' : 'microphone'}</h1>
          <p className="text-white/80">Prepare for your session with {coachName} - {moduleTitle}</p>
        </div>

        <div className="p-6 sm:p-8">
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start"
            >
              <AlertCircle className="h-5 w-5 text-red-500 mr-3 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-red-800 font-medium">Permission needed</p>
                <p className="text-red-600 text-sm mt-1">{error}</p>
              </div>
            </motion.div>
          )}

          <div className="grid md:grid-cols-2 gap-6">
            {/* Video preview (for video mode) */}
            {mode === 'video' && (
              <div className="space-y-4">
                <div className="relative aspect-video bg-gray-900 rounded-lg overflow-hidden">
                  {isLoading ? (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Loader2 className="h-8 w-8 text-gray-400 animate-spin" />
                    </div>
                  ) : isVideoEnabled ? (
                    <video
                      ref={videoRef}
                      autoPlay
                      playsInline
                      muted
                      className="w-full h-full object-cover scale-x-[-1]"
                    />
                  ) : (
                    <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-400">
                      <VideoOff className="h-16 w-16 mb-2" />
                      <p>Camera is off</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Audio visualization and controls */}
            <div className={cn(
              "space-y-6",
              mode === 'voice' && "md:col-span-2 max-w-lg mx-auto w-full"
            )}>
              {/* Audio level indicator */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-700">Microphone</label>
                  <span className="text-xs text-gray-500">
                    {isAudioEnabled ? 'Speak to test' : 'Muted'}
                  </span>
                </div>
                <div className="relative h-12 bg-gray-100 rounded-lg overflow-hidden">
                  <motion.div
                    className="absolute inset-y-0 left-0 bg-primary"
                    animate={{ width: `${audioLevel * 100}%` }}
                    transition={{ duration: 0.1 }}
                  />
                  <div className="absolute inset-0 flex items-center justify-center">
                    {isAudioEnabled ? (
                      <Mic className="h-5 w-5 text-gray-600" />
                    ) : (
                      <MicOff className="h-5 w-5 text-gray-400" />
                    )}
                  </div>
                </div>
              </div>

              {/* Control buttons */}
              <div className="flex gap-3">
                <Button
                  variant={isAudioEnabled ? "default" : "secondary"}
                  size="lg"
                  onClick={toggleAudio}
                  className="flex-1"
                >
                  {isAudioEnabled ? (
                    <>
                      <Mic className="h-4 w-4 mr-2" />
                      Microphone On
                    </>
                  ) : (
                    <>
                      <MicOff className="h-4 w-4 mr-2" />
                      Microphone Off
                    </>
                  )}
                </Button>

                {mode === 'video' && (
                  <Button
                    variant={isVideoEnabled ? "default" : "secondary"}
                    size="lg"
                    onClick={toggleVideo}
                    className="flex-1"
                  >
                    {isVideoEnabled ? (
                      <>
                        <Video className="h-4 w-4 mr-2" />
                        Camera On
                      </>
                    ) : (
                      <>
                        <VideoOff className="h-4 w-4 mr-2" />
                        Camera Off
                      </>
                    )}
                  </Button>
                )}
              </div>

              {/* Device settings */}
              <div className="border-t pt-4">
                <Button
                  variant="ghost"
                  onClick={() => setShowSettings(!showSettings)}
                  className="w-full justify-between"
                >
                  <span className="flex items-center">
                    <Settings className="h-4 w-4 mr-2" />
                    Device Settings
                  </span>
                  <ChevronDown className={cn(
                    "h-4 w-4 transition-transform",
                    showSettings && "rotate-180"
                  )} />
                </Button>

                <AnimatePresence>
                  {showSettings && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden"
                    >
                      <div className="mt-4 space-y-4">
                        {/* Microphone selection */}
                        <div>
                          <label className="text-sm font-medium text-gray-700 block mb-2">
                            Microphone
                          </label>
                          <select
                            value={selectedAudioInput}
                            onChange={(e) => setSelectedAudioInput(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                          >
                            {audioInputs.map(device => (
                              <option key={device.deviceId} value={device.deviceId}>
                                {device.label || 'Microphone'}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Camera selection (video mode only) */}
                        {mode === 'video' && (
                          <div>
                            <label className="text-sm font-medium text-gray-700 block mb-2">
                              Camera
                            </label>
                            <select
                              value={selectedVideoInput}
                              onChange={(e) => setSelectedVideoInput(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                            >
                              {videoInputs.map(device => (
                                <option key={device.deviceId} value={device.deviceId}>
                                  {device.label || 'Camera'}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}

                        {/* Speaker selection */}
                        <div>
                          <label className="text-sm font-medium text-gray-700 block mb-2">
                            Speaker
                          </label>
                          <select
                            value={selectedAudioOutput}
                            onChange={(e) => setSelectedAudioOutput(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                          >
                            {audioOutputs.map(device => (
                              <option key={device.deviceId} value={device.deviceId}>
                                {device.label || 'Speaker'}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>

          {/* Start button */}
          <div className="mt-8 flex justify-end">
            <Button
              size="lg"
              onClick={handleStart}
              disabled={isLoading || !!error}
              className="bg-primary hover:bg-primary/90 text-white px-8"
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Setting up...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Start Session
                </>
              )}
            </Button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}